import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "start-view"
}
const _hoisted_2 = { class: "start-view__illustration" }
const _hoisted_3 = { class: "start-view__content" }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "start-view__setup" }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = {
  key: 1,
  class: "start-view start-view--error"
}
const _hoisted_8 = { key: 1 }

import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBikeStore } from '@/store/bikeStore'
import { usePositionStore } from '@/store/positionStore'
import { useMeasurementStore } from '@/store/measurementStore'
import { useUserStore } from '@/store/userStore'
import { useMqttStore } from '@/store/mqttStore'
import {
  BfButton,
  BfInfoCard,
  BfLoader,
  ButtonState,
  ButtonIconPosition,
} from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'
import { getBikeCategory, getHoodsTypeFromCategory } from '@/utils/utils'
import { PreviewImage } from '@/types/preview'
import { BikeHoodsType } from '@/types/bike'
import TitleBar from '@/components/layout/TitleBar.vue'
import BikeSetupStart from '@/components/preview/BikeSetupStart.vue'
import StartSetup from '@/components/setup/StartSetup.vue'
import cranksetService from '@/service/cranksetService'
import handlebarService from '@/service/handlebarService'
import saddleService from '@/service/saddleService'
import pedalService from '@/service/pedalService'


export default /*@__PURE__*/_defineComponent({
  __name: 'StartView',
  setup(__props) {

const { t } = useI18n()
const router = useRouter()
const bikeStore = useBikeStore()
const positionStore = usePositionStore()
const measurementStore = useMeasurementStore()
const userStore = useUserStore()
const mqttStore = useMqttStore()
const { loggedIn, customerId, dealerId, sessionId } = storeToRefs(userStore)
const { bikeData, bikeNo, bikeHoodsType, bikePreview } = storeToRefs(bikeStore)
const { startPosition } = storeToRefs(positionStore)
const { latestMeasurement } = storeToRefs(measurementStore)
const { connected } = storeToRefs(mqttStore)
const { checkLogin } = userStore

const loading = ref(true)
const error = ref(false)

const averageCrankset = ref()
const averageHandlebar = ref()
const averageSaddle = ref()
const averagePedal = ref()

const fetchAverages = async () => {
  averageCrankset.value = cranksetService.fetchAverageCrankset()
  averageHandlebar.value = await handlebarService.fetchAverageHandlebar({
    sessionId: sessionId?.value,
    bikeHoodsType: bikeHoodsType?.value,
  })
  averageSaddle.value = await saddleService.fetchAverageSaddle({
    sessionId: sessionId?.value,
  })
  averagePedal.value = await pedalService.fetchAveragePedal({
    sessionId: sessionId?.value,
  })
}

const createInitialMeasurement = async () => {
  measurementStore.create({
    crankset: {
      active: averageCrankset.value,
    },
    handlebar: {
      active: averageHandlebar.value,
      x: startPosition.value.handlebarX,
      y: startPosition.value.handlebarY,
    },
    saddle: {
      active: averageSaddle.value,
      x: startPosition.value.saddleX,
      y: startPosition.value.saddleY,
    },
    pedal: {
      active: averagePedal.value,
    },
  })
}

const getInitialDistance = () => {
  bikeStore.updateXY(
    customerId.value,
    dealerId.value,
    sessionId.value,
    latestMeasurement.value,
    false,
  )
}

const login = () => {
  if (router.currentRoute.value.query.bikeNo) {
    bikeNo.value = Number.parseInt(
      router.currentRoute.value.query.bikeNo as string,
    )
  }

  if (router.currentRoute.value.query.customerId) {
    customerId.value = router.currentRoute.value.query.customerId as string
  }

  if (router.currentRoute.value.query.sessionId) {
    checkLogin(router.currentRoute.value.query.sessionId as string)
      .then(() => {
        error.value = false

        bikeStore
          .retrieveBikes(customerId.value, dealerId.value, sessionId.value)
          .then(async () => {
            loading.value = false

            if (bikeData && bikeData.value) {
              startPosition.value = {
                saddleX: bikeData.value?.SF_SADDLE_CENTER_X,
                saddleY: bikeData.value?.SF_SADDLE_CENTER_Y,
                handlebarX: bikeData.value?.SF_HANDLEBAR_CENTER_X * -1,
                handlebarY: bikeData.value?.SF_HANDLEBAR_CENTER_Y,
              }

              const bikeCategory = getBikeCategory(
                bikeData.value?.MAIN_BIKETYPE_NAME,
                bikeData.value?.SUB_BIKETYPE_NAME,
              )
              bikeHoodsType.value = getHoodsTypeFromCategory(bikeCategory)

              switch (bikeHoodsType.value) {
                case BikeHoodsType.DROPBAR:
                  bikePreview.value = PreviewImage.DROPBAR_BIKE
                  break
                case BikeHoodsType.TRIBAR:
                  bikePreview.value = PreviewImage.TRIBAR_BIKE
                  break
                case BikeHoodsType.FLATBAR:
                  bikePreview.value = PreviewImage.FLATBAR_BIKE
                  break
                default:
                  bikePreview.value = PreviewImage.DROPBAR_BIKE
              }
            }

            if (!latestMeasurement.value) {
              await fetchAverages()
              createInitialMeasurement()
              createInitialMeasurement()
              getInitialDistance()
            }
          })
      })
      .catch(() => {
        loading.value = false
        error.value = true
      })
  } else {
    loading.value = false
  }
}

const hasStartPosition = computed(
  () =>
    startPosition?.value?.handlebarX &&
    startPosition?.value?.handlebarY &&
    startPosition?.value?.saddleX &&
    startPosition?.value?.saddleY,
)

if (
  (router.currentRoute.value.query.customerId &&
    router.currentRoute.value.query.customerId !== customerId.value) ||
  (router.currentRoute.value.query.bikeNo &&
    router.currentRoute.value.query.bikeNo !== bikeNo.value)
) {
  userStore.logout()
}

if (loggedIn.value) {
  loading.value = false
} else {
  login()
}

const next = () => {
  router.push({ name: 'setup' })
}

return (_ctx: any,_cache: any) => {
  return (_unref(loggedIn))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (hasStartPosition.value)
            ? (_openBlock(), _createBlock(BikeSetupStart, { key: 0 }))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(TitleBar, {
            title: _unref(t)('steps.start')
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BfButton), {
                icon: "/icons/52-Arrows-Diagrams/01-Arrows/arrow-right-1.svg",
                "icon-position": _unref(ButtonIconPosition).Right,
                state: _unref(connected) ? _unref(ButtonState).Enabled : _unref(ButtonState).Disabled,
                onClick: next
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('common.next')), 1)
                ]),
                _: 1
              }, 8, ["icon-position", "state"])
            ]),
            _: 1
          }, 8, ["title"]),
          (loading.value)
            ? (_openBlock(), _createBlock(_unref(BfLoader), { key: 0 }))
            : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (!_unref(connected))
                  ? (_openBlock(), _createBlock(_unref(BfInfoCard), {
                      key: 0,
                      title: _unref(t)('start.notConnected.index')
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('start.notConnected.content')), 1)
                      ]),
                      _: 1
                    }, 8, ["title"]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_5, [
                  (hasStartPosition.value)
                    ? (_openBlock(), _createBlock(StartSetup, { key: 0 }))
                    : (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(_unref(t)('start.noStartPosition')), 1))
                ])
              ]))
        ])
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_7, [
        (loading.value && !error.value)
          ? (_openBlock(), _createBlock(_unref(BfLoader), { key: 0 }))
          : (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createElementVNode("h1", null, _toDisplayString(_unref(t)('start.session.unknown.title')), 1),
              _createElementVNode("p", null, _toDisplayString(_unref(t)('start.session.unknown.content')), 1)
            ]))
      ]))
}
}

})